import React from "react";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@bcc/components/ui/tooltip";
import { useUserProfileState } from "@client-shared/providers/user-profile";
import { Building2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { setRoasterLogoTestId } from "./roaster-logo.utils";

interface RoasterLogoProps {
  readonly testId?: string;
}

export function RoasterLogo({ testId }: RoasterLogoProps) {
  const [{ profile }] = useUserProfileState();
  const [imageError, setImageError] = React.useState(false);
  const [lastLogoUrl, setLastLogoUrl] = React.useState(profile?.vendorLogoUrl);
  const { t } = useTranslation();

  if (profile?.vendorLogoUrl !== lastLogoUrl) {
    setLastLogoUrl(profile?.vendorLogoUrl);
    setImageError(false);
  }

  // Show placeholder if no logo or image failed to load
  if (!profile?.vendorLogoUrl || imageError) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              data-testid={testId ?? setRoasterLogoTestId("placeholder")}
              className="flex h-8 w-8 items-center justify-center rounded-md bg-muted"
            >
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">{t("layout.roasterLogo.addLogoInSettings")}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const roasterName = profile.name ?? t("layout.roasterLogo.unknownRoaster");

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            data-testid={testId ?? setRoasterLogoTestId("container")}
            className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-md border border-border-subtle bg-muted"
          >
            <img
              data-testid={setRoasterLogoTestId("image")}
              src={profile.vendorLogoUrl || "/placeholder.svg"}
              alt={t("layout.roasterLogo.logoAlt", { roasterName })}
              className="h-full w-full object-contain"
              onError={() => {
                setImageError(true);
              }}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">
            {t("layout.roasterLogo.managedBy", {
              roasterName,
            })}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
